import { useMemo, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useStockEditRequests,
  type StockEditRequestStatusFilter,
} from "@/hooks/queries/useStockEditRequests";
import { useStockArticles } from "@/hooks/queries/useStockArticles";
import { useUsers } from "@/hooks/queries/useUsers";
import { useApproveStockEditRequestMutation } from "@/hooks/mutations/useApproveStockEditRequest";
import { useRejectStockEditRequestMutation } from "@/hooks/mutations/useRejectStockEditRequest";
import type {
  StockAdjustmentChanges,
  StockArticleFieldChanges,
  StockEditRequest,
} from "@/lib/stock.api";

const STATUS_TABS: { value: StockEditRequestStatusFilter; label: string }[] = [
  { value: "pending", label: "En attente" },
  { value: "approved", label: "Approuvées" },
  { value: "rejected", label: "Refusées" },
  { value: "all", label: "Toutes" },
];

const STATUS_LABEL: Record<StockEditRequest["status"], string> = {
  pending: "En attente",
  approved: "Approuvée",
  rejected: "Refusée",
};

const FIELD_LABELS: Record<string, string> = {
  name: "Nom",
  unit: "Unité",
  defaultSellingPrice: "Prix de vente par défaut",
  defaultSupplierPrice: "Prix fournisseur par défaut",
  minLevel: "Seuil bas",
  quantity: "Quantité d'ajustement",
};

function targetLabel(t: StockEditRequest["targetType"]): string {
  return t === "article_fields" ? "Fiche article" : "Ajustement de stock";
}

function StatusBadge({ status }: { status: StockEditRequest["status"] }) {
  const cls =
    status === "pending"
      ? "bg-warning-bg text-warning-text border-warning-border"
      : status === "approved"
        ? "bg-success-bg text-success-text border-success-border"
        : "bg-danger-bg text-danger-text border-danger-border";
  return (
    <span className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

function formatDateTime(value?: string): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function StockEditQueuePage() {
  const [statusFilter, setStatusFilter] = useState<StockEditRequestStatusFilter>("pending");
  const [selected, setSelected] = useState<StockEditRequest | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  const { data: requests = [], isLoading } = useStockEditRequests(statusFilter);
  const { data: articles = [] } = useStockArticles(true);
  const { data: usersResp } = useUsers({});

  const approveMutation = useApproveStockEditRequestMutation();
  const rejectMutation = useRejectStockEditRequestMutation();
  const busy = approveMutation.isPending || rejectMutation.isPending;

  usePageHeader({ title: "Demandes de modification (stock)" });

  const articleName = useMemo(() => {
    const map = new Map(articles.map((a) => [a.id, a.name]));
    return (id: number) => map.get(id) ?? `Article #${id}`;
  }, [articles]);

  const userName = useMemo(() => {
    const map = new Map((usersResp?.data ?? []).map((u) => [u.id, u.fullName]));
    return (id: number) => map.get(id) ?? `Utilisateur #${id}`;
  }, [usersResp]);

  const columns = useMemo<ColumnDef<StockEditRequest, any>[]>(
    () => [
      { accessorKey: "id", header: "#", cell: ({ row }) => `#${row.original.id}` },
      {
        id: "article",
        header: "Article",
        cell: ({ row }) => articleName(row.original.stockArticleId),
      },
      {
        id: "target",
        header: "Type",
        cell: ({ row }) => targetLabel(row.original.targetType),
      },
      {
        id: "requestedBy",
        header: "Demandé par",
        cell: ({ row }) => userName(row.original.requestedBy),
      },
      {
        id: "status",
        header: "Statut",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "createdAt",
        header: "Date",
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
    ],
    [articleName, userName],
  );

  function closeDetail() {
    setSelected(null);
    setRejecting(false);
    setRejectNote("");
  }

  function handleApprove() {
    if (!selected) return;
    approveMutation.mutate(selected.id, { onSuccess: closeDetail });
  }

  function handleReject() {
    if (!selected) return;
    rejectMutation.mutate(
      { requestId: selected.id, note: rejectNote.trim() || undefined },
      { onSuccess: closeDetail },
    );
  }

  const changeRows: [string, unknown][] = selected
    ? selected.targetType === "stock_adjustment"
      ? [["quantity", (selected.proposedChanges as StockAdjustmentChanges).quantity]]
      : Object.entries(selected.proposedChanges as StockArticleFieldChanges)
    : [];

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setStatusFilter(tab.value)}
            className={`rounded-full border px-3 py-1 text-[12px] font-medium ${
              statusFilter === tab.value
                ? "border-brand-gold-dark bg-brand-gold-dark text-white"
                : "border-border bg-card text-muted-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={requests}
        loading={isLoading}
        emptyMessage="Aucune demande."
        onRowClick={(r) => {
          setSelected(r);
          setRejecting(false);
          setRejectNote("");
        }}
        pageSize={15}
      />

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex flex-col gap-2 pr-8 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <DialogTitle className="text-[17px] font-bold text-foreground">
                      Demande #{selected.id}
                    </DialogTitle>
                    <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                      {targetLabel(selected.targetType)} — {articleName(selected.stockArticleId)} —
                      demandée par {userName(selected.requestedBy)}
                    </DialogDescription>
                  </div>
                  <StatusBadge status={selected.status} />
                </div>
              </DialogHeader>

              <div className="grid gap-3 sm:grid-cols-3 text-[12px]">
                <div>
                  <p className="text-muted-foreground">Demandée</p>
                  <p className="text-body">{formatDateTime(selected.createdAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Révisée</p>
                  <p className="text-body">
                    {selected.status === "pending" ? "Non révisée" : formatDateTime(selected.reviewedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Réviseur</p>
                  <p className="text-body">
                    {selected.reviewedBy ? userName(selected.reviewedBy) : "Non assigné"}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Raison
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-body">{selected.reason}</p>
              </div>

              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-left">
                  <thead className="bg-surface-muted">
                    <tr>
                      <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Champ
                      </th>
                      <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Proposition
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeRows.map(([key, value]) => (
                      <tr key={key} className="border-t border-border">
                        <td className="px-4 py-2 text-[12px] text-body">
                          {FIELD_LABELS[key] ?? key}
                        </td>
                        <td className="px-4 py-2 text-[12px] font-medium text-success-text">
                          {String(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selected.reviewNote && (
                <div className="rounded-lg border border-danger-border bg-danger-bg px-4 py-3">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wide text-danger-text">
                    Note de révision
                  </p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-danger-text">
                    {selected.reviewNote}
                  </p>
                </div>
              )}

              {selected.status === "pending" && rejecting && (
                <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
                  <Input
                    placeholder="Motif du refus (optionnel)"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                  />
                </div>
              )}

              <DialogFooter>
                {selected.status === "pending" ? (
                  rejecting ? (
                    <>
                      <Button variant="secondary" onClick={() => setRejecting(false)} disabled={busy}>
                        Annuler
                      </Button>
                      <Button variant="destructive" onClick={handleReject} disabled={busy}>
                        {busy ? <Loader2 size={13} className="animate-spin" /> : "Confirmer le refus"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setRejecting(true)} disabled={busy}>
                        <X size={13} /> Refuser
                      </Button>
                      <Button onClick={handleApprove} disabled={busy}>
                        {busy ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <Check size={13} />
                        )}
                        Approuver
                      </Button>
                    </>
                  )
                ) : (
                  <Button variant="secondary" onClick={closeDetail}>
                    Fermer
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
